import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type * as Monaco from 'monaco-editor';
import { parser as pythonParser } from '@lezer/python';

/**
 * 验证结果
 */
export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

/**
 * 验证错误详情
 */
export interface ValidationError {
  line: number;
  column: number;
  endLine: number;
  endColumn: number;
  message: string;
  severity: 'error' | 'warning' | 'info';
  source?: string;
}

/**
 * 根据字符偏移计算行号和列号（Monaco 行号从 1 开始，列号从 1 开始）
 */
function offsetToLineCol(
  code: string,
  offset: number
): { line: number; column: number } {
  let line = 1;
  let lastNewline = -1;
  for (let i = 0; i < offset && i < code.length; i++) {
    if (code[i] === '\n') {
      line++;
      lastNewline = i;
    }
  }
  return { line, column: offset - lastNewline };
}

/**
 * Python 3 语法检查（基于 @lezer/python — CodeMirror 6 的 Python parser）
 * 完整支持 Python 3.10+ 语法：类型注解、TypedDict、match-case、walrus operator 等
 * 通过遍历 AST 中的 Error 节点检测语法错误，无需任何预处理
 */
function validatePythonSyntax(code: string): ValidationResult {
  try {
    const tree = pythonParser.parse(code);
    const errors: ValidationError[] = [];

    // 遍历 AST，收集所有 Error 节点
    tree.iterate({
      enter(node) {
        if (node.type.isError) {
          const start = offsetToLineCol(code, node.from);
          const end = offsetToLineCol(code, node.to);

          errors.push({
            line: start.line,
            column: start.column,
            endLine: end.line,
            endColumn: end.column,
            message: '语法错误',
            severity: 'error',
            source: 'lezer-python',
          });
        }
      },
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings: [],
    };
  } catch (error) {
    console.error('Python validation error:', error);
    return { valid: true, errors: [], warnings: [] };
  }
}

/**
 * 从 Monaco Marker 转换为 ValidationError
 */
function monacoMarkerToValidationError(
  marker: Monaco.editor.IMarker
): ValidationError {
  return {
    line: marker.startLineNumber,
    column: marker.startColumn,
    endLine: marker.endLineNumber,
    endColumn: marker.endColumn,
    message: marker.message,
    severity:
      marker.severity === 8
        ? 'error'
        : marker.severity === 4
          ? 'warning'
          : 'info',
    source: marker.source || marker.owner,
  };
}

/**
 * Monaco Editor 语法验证 Hook
 *
 * @example
 * ```tsx
 * const { validate, validationResult, isValidating } = useMonacoSyntaxValidation({
 *   editor: editorRef.current,
 *   monaco: monacoRef.current,
 *   language: 'typescript',
 * });
 *
 * // 手动触发验证
 * const handleValidate = () => {
 *   validate();
 * };
 * ```
 */
export function useMonacoSyntaxValidation(options: {
  /** 直接传 useRef 创建的 ref 对象（不是 .current），确保 validate 时能读到最新值 */
  editorRef: RefObject<Monaco.editor.IStandaloneCodeEditor | null>;
  monacoRef: RefObject<typeof Monaco | null>;
  language?: string;
  autoValidate?: boolean; // 是否在代码变化时自动验证
}) {
  const { editorRef, monacoRef, autoValidate = false } = options;

  // language 可能随渲染改变，用 ref 同步最新值
  const languageRef = useRef(options.language ?? 'typescript');
  languageRef.current = options.language ?? 'typescript';

  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const validationTimeoutRef = useRef<NodeJS.Timeout>();

  /**
   * 执行验证：直接读 ref.current，无论是否重渲染都能拿到最新 editor/monaco
   */
  const validate = useCallback((): ValidationResult | null => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const language = languageRef.current;
    if (!editor || !monaco) {
      return null;
    }

    setIsValidating(true);

    const model = editor.getModel();
    if (!model) {
      setIsValidating(false);
      return null;
    }

    const code = model.getValue();

    try {
      let result: ValidationResult;

      if (language === 'python') {
        // Python: 使用 dt-python-parser 验证（完整 Python 3 语法支持）
        result = validatePythonSyntax(code);

        // 如果验证失败，手动设置 Monaco markers 以在编辑器中显示错误
        if (!result.valid && result.errors.length > 0) {
          monaco.editor.setModelMarkers(
            model,
            'python-syntax',
            result.errors.map(err => ({
              startLineNumber: err.line,
              startColumn: err.column,
              endLineNumber: err.endLine,
              endColumn: err.endColumn,
              message: err.message,
              severity: monaco.MarkerSeverity.Error,
            }))
          );
        } else {
          // 清除之前的 markers
          monaco.editor.setModelMarkers(model, 'python-syntax', []);
        }
      } else {
        // TypeScript/JavaScript: 使用 Monaco 内置验证
        // 等待一小段时间让 TypeScript Worker 完成验证
        const markers = monaco.editor.getModelMarkers({ resource: model.uri });

        const errors = markers
          .filter(m => m.severity === monaco.MarkerSeverity.Error)
          .map(monacoMarkerToValidationError);

        const warnings = markers
          .filter(m => m.severity === monaco.MarkerSeverity.Warning)
          .map(monacoMarkerToValidationError);

        result = {
          valid: errors.length === 0,
          errors,
          warnings,
        };
      }

      setValidationResult(result);
      setIsValidating(false);
      return result;
    } catch (error) {
      console.error('Validation error:', error);
      const fallbackResult: ValidationResult = {
        valid: false,
        errors: [
          {
            line: 1,
            column: 1,
            endLine: 1,
            endColumn: 100,
            message: '验证过程出错',
            severity: 'error',
          },
        ],
        warnings: [],
      };
      setValidationResult(fallbackResult);
      setIsValidating(false);
      return fallbackResult;
    }
  }, []); // 无依赖，始终从 optionsRef 读取最新值

  /**
   * 延迟验证（用于自动验证）
   */
  const debouncedValidate = useCallback(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    validationTimeoutRef.current = setTimeout(() => {
      validate();
    }, 500); // 500ms 防抖
  }, [validate]);

  /**
   * 自动验证：监听代码变化
   */
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!autoValidate || !editor || !monaco) {
      return;
    }

    const disposable = editor.onDidChangeModelContent(() => {
      debouncedValidate();
    });

    // 初始验证
    debouncedValidate();

    return () => {
      disposable.dispose();
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [autoValidate, debouncedValidate]); // 依赖 autoValidate 和 debouncedValidate，editor/monaco 从 ref 读取

  /**
   * 监听 Monaco 的 marker 变化（用于 TypeScript/JavaScript）
   */
  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    const language = languageRef.current;
    if (!editor || !monaco || language === 'python') {
      return;
    }

    const model = editor.getModel();
    if (!model) return;

    const disposable = monaco.editor.onDidChangeMarkers(
      (uris: readonly Monaco.Uri[]) => {
        const uri = uris[0];
        if (uri && uri.toString() === model.uri.toString() && autoValidate) {
          // TypeScript 验证结果更新了，自动刷新
          validate();
        }
      }
    );

    return () => {
      disposable.dispose();
    };
  }, [autoValidate, validate]); // editor/monaco/language 从 optionsRef 读取

  return {
    validate,
    validationResult,
    isValidating,
  };
}
